// Libraries
import ExcelJS, { type Workbook } from 'exceljs';
// Services
import { EeoiVessel } from '@services/apiGateway';
// Helpers
import { formatDateToUtc } from '@helpers/index';

/** Excel number format: integers with thousands separator, e.g. 1,234 */
const FMT_INT = '#,##0';
/** Excel number format: 1 decimal place with thousands separator, e.g. 1,234.5 */
const FMT_1DP = '#,##0.0';

/** Column definition for the EEOI export sheet. */
type EeoiExportColumn = {
  /** Header label displayed in row 1 */
  displayName: string;
  /** Key of EeoiVessel to read the value from */
  key: keyof EeoiVessel;
  /**
   * ExcelJS numFmt string to apply to the cell.
   * When omitted the raw value is written as-is (strings, plain integers).
   */
  numFmt?: string;
  /** Optional value transformer for non-numeric fields (e.g. date formatting) */
  transform?: (value: number | string) => number | string | null;
};

/** Column definitions mirroring the EeoiScoresTable column groups and decimal precision */
const EEOI_EXPORT_COLUMNS: EeoiExportColumn[] = [
  // Vessel Details
  { displayName: 'IMO', key: 'imo' },
  { displayName: 'Vessel Name', key: 'vesselName' },
  { displayName: 'Year Built', key: 'yearBuilt' },
  { displayName: 'Vessel Type', key: 'vesselType' },
  { displayName: 'Shipyard', key: 'shipyard' },
  {
    displayName: 'Last Noon Report',
    key: 'lastNoonReportDate',
    transform: (v) => formatDateToUtc(v as string),
  },
  { displayName: 'Capacity (DWT)', key: 'deadWeightTonnage', numFmt: FMT_INT },
  // Operational Details
  { displayName: 'Ton-Mile', key: 'cargoTimesDistance', numFmt: FMT_INT },
  { displayName: 'Voyages', key: 'voyages' },
  { displayName: 'Distance Sailed (NM)', key: 'totalDistance', numFmt: FMT_1DP },
  { displayName: 'FOC (MT)', key: 'foc', numFmt: FMT_1DP },
  { displayName: 'EEOI Score', key: 'eeoiScore', numFmt: FMT_1DP },
  // Well To Tank
  { displayName: 'WTT - CO2e (MT)', key: 'wellToTank', numFmt: FMT_1DP },
  // Tank To Wake
  { displayName: 'TTW - CO2 (MT)', key: 'co2', numFmt: FMT_1DP },
  { displayName: 'TTW - CH4 CO2e (MT)', key: 'ch4', numFmt: FMT_1DP },
  { displayName: 'TTW - N2O CO2e (MT)', key: 'n2o', numFmt: FMT_1DP },
  { displayName: 'TTW - CO2e (MT)', key: 'co2e', numFmt: FMT_1DP },
  // Well To Wake
  { displayName: 'WTW - CO2e (MT)', key: 'wellToWake', numFmt: FMT_1DP },
];

/**
 * Builds an Excel workbook with a single summary sheet of EEOI fleet score data.
 * Only the vessels whose IMO numbers are in `selectedImos` are included (i.e. visible rows).
 *
 * @class BuildEeoiExportFile
 * @property {Workbook} workbook - The ExcelJS workbook instance ready for download.
 *
 * @constructor
 * @param {EeoiVessel[]} fleet - Full array of EEOI vessel data.
 * @param {number[]} selectedImos - IMO numbers of the vessels currently visible in the table.
 *
 * @example
 * const exportFile = new BuildEeoiExportFile(fleet, selectedImos);
 * const buffer = await exportFile.workbook.xlsx.writeBuffer();
 */
export class BuildEeoiExportFile {
  workbook: Workbook;

  constructor(fleet: EeoiVessel[], selectedImos: number[]) {
    const creationDate = new Date();
    const selectedFleet = fleet
      .filter(({ imo }) => selectedImos.includes(imo))
      .sort((a, b) => a.vesselName.localeCompare(b.vesselName));

    this.workbook = new ExcelJS.Workbook();
    this.workbook.creator = 'Bearing AI';
    this.workbook.lastModifiedBy = 'Bearing AI';
    this.workbook.created = creationDate;
    this.workbook.modified = creationDate;

    this.createSummarySheet(selectedFleet);
  }

  /**
   * Creates the EEOI summary sheet: one header row followed by one row per vessel.
   *
   * @param {EeoiVessel[]} fleet - Filtered array of vessels to include.
   * @returns {void}
   */
  private createSummarySheet = (fleet: EeoiVessel[]) => {
    const worksheet = this.workbook.addWorksheet('EEOI Summary', {
      views: [{ state: 'frozen', xSplit: 0, ySplit: 1 }],
    });

    // Write header row
    EEOI_EXPORT_COLUMNS.forEach(({ displayName }, colIdx) => {
      const cell = worksheet.getCell(`${this.numberToCol(colIdx + 1)}1`);
      Object.assign(cell, { value: displayName, font: { bold: true } });
    });

    // Write data rows
    fleet.forEach((vessel, rowIdx) => {
      EEOI_EXPORT_COLUMNS.forEach(({ key, numFmt, transform }, colIdx) => {
        const raw = vessel[key];
        const value = transform ? transform(raw as number | string) : raw;
        const cell = worksheet.getCell(`${this.numberToCol(colIdx + 1)}${rowIdx + 2}`);
        cell.value = value ?? null;
        if (numFmt) cell.numFmt = numFmt;
      });
    });

    this.autoFitColumns(worksheet);
  };

  /**
   * Converts a 1-based column number to its corresponding Excel column letter(s).
   *
   * @param {number} num - Column index (1-based).
   * @returns {string} Excel column letter(s), e.g. 1 → "A", 27 → "AA".
   */
  private numberToCol = (num: number): string => {
    let col = '';
    while (num > 0) {
      const remainder = (num - 1) % 26;
      col = String.fromCharCode(65 + remainder) + col;
      num = Math.floor((num - 1) / 26);
    }
    return col;
  };

  /**
   * Auto-fits all columns in the given worksheet based on cell content length.
   *
   * @param {ExcelJS.Worksheet} worksheet - The worksheet to auto-fit.
   * @returns {void}
   */
  private autoFitColumns = (worksheet: ExcelJS.Worksheet) => {
    worksheet.columns?.forEach((column) => {
      let maxLength = 8;
      if (column.eachCell) {
        column.eachCell({ includeEmpty: true }, (cell) => {
          cell.font = { ...cell.font, name: 'Consolas' };
          const cellText = cell.text;
          maxLength = Math.max(maxLength, cellText.length);
        });
        column.width = maxLength + 2;
      }
    });
  };
}
